import { createTheme } from '../components/mui/Styles/index'
import '../fonts/index.css'

const theme = createTheme({
  palette: {
    common: {
      black: '#000000',
      white: '#ffffff',
    },
    primary: {
      main: '#145a4b',
      light: '#1d846e',
      dark: '#0b3028',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#2db47d',
      light: '#44d097',
      dark: '#238b61',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ffe16e',
      light: '#ffeca1',
      dark: '#ffd63b',
      contrastText: '#ffffff',
    },
    info: {
      main: '#5488db',
      light: '#7ea5e4',
      dark: '#2c6bd0',
      contrastText: '#ffffff',
    },
    error: {
      main: '#cc3f0c',
      light: '#f2531a',
      dark: '#9c3009',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2a7b5a',
      light: '#37a176',
      dark: '#1d553e',
      contrastText: '#ffffff',
    },
    white: {
      50: 'rgba(255, 255, 255, 0.5)',
    },
    shadow: {
      dark: 'rgba(0, 0, 0, 0.12)',
    },
    grey: {
      50: '#f2f2f2',
      300: '#bebebe',
      400: '#e7e7e7',
      500: '#b7b7b7',
      700: '#606060',
      900: '#262626',
    },
    background: {
      paper: '#ffffff',
      default: '#f2f5f2',
    },
    text: {
      primary: '#000000',
    },
  },
  custom: {
    disabled: { main: '#676767' },
    linkGreen: { main: '#2db47d' },
    dateInputBackground: { main: 'rgba(255,255,255,.5) !important' },
  },
  shape: {
    borderRadius: 2,
  },
  spacing: 4,
  typography: {
    fontFamily: 'AtlasGroteskRegular',
    allVariants: {
      fontFamily: [
        'AtlasGroteskBold',
        'AtlasGroteskLight',
        'AtlasGroteskRegular',
        'AtlasTypewriterLight',
        'AtlasTypewriterMedium',
        'AtlasTypewriterRegular',
        'SanomatMedium',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      fontSize: 16,
    },
    h3: {
      fontSize: '1.5rem',
      color: '#145a4b !important',
      fontFamily: 'AtlasGroteskRegular',
      fontWeight: '600',
    },
    h5: {
      fontSize: '1.25rem',
      color: '#145a4b !important',
      fontFamily: 'SanomatMedium',
    },
    h6: {
      fontSize: '14px',
      fontFamily: 'AtlasGroteskBold',
      color: '#000000 !important',
    },
    label: {
      fontSize: '16px',
      fontFamily: 'AtlasGroteskRegular',
      color: '#000000 !important',
    },
    body1: {
      fontSize: '14px',
      fontFamily: 'AtlasGroteskRegular',
      color: '#000000',
    },
    body2: {
      fontSize: '15px',
      fontFamily: 'AtlasGroteskRegular',
      color: '#000000',
    },
    link: {
      fontSize: '0.813rem',
      fontFamily: 'AtlasGroteskRegular',
      color: '#2db47d !important',
    },
    errorMessage: {
      fontSize: '1rem',
      fontFamily: 'AtlasGroteskRegular',
      fontWeight: 'bold',
      color: '#145a4b !important',
      letterSpacing: '.5px',
      display: 'flex',
    },
    cardHeader: {
      fontFamily: 'SanomatMedium',
      fontSize: '25px',
      fontWeight: '500',
      lineHeight: '1',
      color: '#145a4b',
    },
    cardMessage: {
      fontFamily: 'AtlasGroteskRegular',
      fontSize: '16px',
      lineHeight: '1.25',
      color: '#000',
    },
    message: {
      fontSize: '16px',
      fontFamily: 'AtlasGroteskRegular',
      color: '#000000 !important',
    },
  },
  dataGridMiniTable: {
    overflowX: 'hidden',
    mt: 2,
    '& .MuiDataGrid-cell:nth-of-type(2), & .MuiDataGrid-columnHeader:nth-of-type(2)': {
      paddingLeft: '16px !important',
    },
    '& .MuiDataGrid-cell:nth-last-of-type(2), & .MuiDataGrid-columnHeader:nth-last-of-type(3)': {
      paddingRight: '16px !important',
    },
  },
  soloContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '95vh',
    justifyContent: 'center',
    alignItems: 'center',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 900,
      md: 1240,
      lg: 1536,
      xl: 1730,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: '0px',
          height: '100%',
        },
        html: {
          height: '100%',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          '&.soloCard': {
            width: '496px',
            padding: '25px 0px',
            borderRadius: '12px !important',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3) !important',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontSize: '15px',
          '& Button': {
            fontSize: '15px',
          },
        },
        message: {
          alignContent: 'end',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          marginLeft: '-5px',
          paddingLeft: '7px',
          paddingRight: '7px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: '48px',
          borderRadius: '4px',
          borderColor: '#606060',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          backgroundColor: 'var(--white-0)',
          marginTop: '12px',
          marginBottom: '12px',
          '& label.Mui-focused': {
            color: '#2db47d',
          },
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: '#2db47d',
            },
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: '#606060',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'AtlasTypewriterRegular',
          fontWeight: 300,
          height: 40,
          letterSpacing: '1.28px',
          borderRadius: '20px',
          textTransform: 'none',
          padding: '6px 24px',
          '&.MuiButton-contained': {
            color: '#ffffff',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {},
        toolbar: {
          display: 'flex',
        },
        selectLabel: {},
        select: {
          borderColor: '#bebebe',
          borderWidth: '1px',
          borderRadius: '4px',
          borderStyle: 'solid',
          paddingTop: '6px',
          paddingBottom: '3px',
        },
        spacer: { order: 3 },
        displayedRows: {
          order: 4,
          '&:before': { content: '"Showing "' },
          '&:after': { content: '" rows"' },
        },
        actions: { order: 5 },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          right: '24px !important',
          width: 'auto',
          borderRadius: '4px',
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          width: '100%',
          backgroundColor: 'inherit',
        },
        message: {
          fontFamily: 'AtlasGroteskRegular',
          fontSize: '15px',
          alignSelf: 'end',
        },
        action: {
          fontFamily: 'AtlasGroteskRegular',
          fontSize: '15px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontFamily: 'AtlasGroteskRegular',
          fontSize: '15px',
          paddingTop: '4px',
        },
      },
      variants: [
        {
          props: { variant: 'inProgress' },
          style: {
            backgroundColor: '#5488db',
            color: '#fff',
          },
        },
        {
          props: { variant: 'partiallyPaid' },
          style: {
            backgroundColor: '#5488db',
            color: '#fff',
          },
        },
        {
          props: { variant: 'readyToPlan' },
          style: {
            backgroundColor: '#5488db',
            color: '#fff',
          },
        },
        {
          props: { variant: 'planned' },
          style: {
            backgroundColor: '#5488db',
            color: '#fff',
          },
        },
        {
          props: { variant: 'released' },
          style: {
            backgroundColor: '#5488db',
            color: '#fff',
          },
        },
        {
          props: { variant: 'pickingInProgress' },
          style: {
            backgroundColor: '#5488db',
            color: '#fff',
          },
        },
        {
          props: { variant: 'picked' },
          style: {
            backgroundColor: '#5488db',
            color: '#fff',
          },
        },
        {
          props: { variant: 'packed' },
          style: {
            backgroundColor: '#5488db',
            color: '#fff',
          },
        },
        {
          props: { variant: 'shipped' },
          style: {
            backgroundColor: '#2a7b5a',
            color: '#fff',
          },
        },
        {
          props: { variant: 'paid' },
          style: {
            backgroundColor: '#2a7b5a',
            color: '#fff',
          },
        },
        {
          props: { variant: 'unpaid' },
          style: {
            backgroundColor: '#ffe16e',
            color: '#000',
          },
        },
        {
          props: { variant: 'overdue' },
          style: {
            backgroundColor: '#cc3f0c',
            color: '#fff',
          },
        },
        {
          props: { variant: 'unfilled' },
          style: {
            backgroundColor: '#ffb978',
            color: '#000',
          },
        },
        {
          props: { variant: 'hold' },
          style: {
            backgroundColor: '#f7cb15',
            color: '#000',
          },
        },
        {
          props: { variant: 'partialHold' },
          style: {
            backgroundColor: '#f7cb15',
            color: '#000',
          },
        },
        {
          props: { variant: 'canceled' },
          style: {
            backgroundColor: '#cfcfcf',
            color: '#000',
          },
        },
        {
          props: { variant: 'invited' },
          style: {
            backgroundColor: '#5488db',
            color: '#fff',
          },
        },
        {
          props: { variant: 'active' },
          style: {
            backgroundColor: '#2a7b5a',
            color: '#fff',
          },
        },
        {
          props: { variant: 'deactivated' },
          style: {
            backgroundColor: '#cc3f0c',
            color: '#fff',
          },
        },
      ],
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: '1925px !important',
          minWidth: '1240px',
          padding: '0 32px !important',
          '@media (min-width: 1536px)': {
            padding: '0 100px !important',
          },
          '@media (min-width: 1736px)': {
            padding: '0 200px !important',
          },
          '&.pageLayout': {
            padding: '12px',
          },
        },
        maxWidthXl: {
          marginTop: '25px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderColor: '#bebebe',
          fontFamily: 'AtlasGroteskRegular',
          fontSize: '15px',
          color: '#000000',
        },
        head: {
          fontWeight: 'bold',
        },
        cellContent: {},
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          backgroundColor: '#ffffff',
          borderColor: '#bebebe',
          borderRadius: '4px',
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-row.Mui-hovered': {
            backgroundColor: 'transparent',
          },
        },
        row: {
          '& .MuiDataGrid-cell:nth-of-type(2)': {
            paddingLeft: '25px',
          },
          '& .MuiDataGrid-cell:nth-last-of-type(2)': {
            paddingRight: '25px',
          },
        },
        cell: {
          '& .textRight': {
            paddingRight: '20px',
          },
        },
        cellContent: {
          color: '#000000',
        },
        columnHeaders: {
          '& .MuiDataGrid-columnHeader:nth-of-type(2)': {
            paddingLeft: '25px',
          },
          '& .MuiDataGrid-columnHeader:nth-last-of-type(3)': {
            paddingRight: '25px',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#ffffff',
          },
        },
        columnHeaderTitle: {
          fontSize: '14px',
          fontFamily: 'AtlasGroteskBold',
          color: '#000000 !important',
          paddingTop: '5px',
        },
        columnSeparator: {
          // display: 'none',
        },
        footerContainer: {
          display: 'block',
        },
        menuIcon: {
          display: 'none',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          color: '#fff',
          zIndex: 2,
        },
      },
    },
  },
})

export default theme
